import React from 'react';
import axios from 'axios';

import Button from '~/components/core/Atomic/Buttons/Button';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import Text from '~/components/core/TextComponents/Text';
import ChevronLeftIcon from '~/components/icons/ChevronLeftIcon';
import useDataFetcher from '~/components/useDataFetcher';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';

import ConductorSection from './Conductor/ConductorSection';
import ExpertsCard from './Experts/ExpertsCard';
import AgentConfigChip, { AGENT_CHIP_TONE } from './AgentConfigChip';
import copies from './copies';
import type {
  AiExpertScopeConfigurationInfo,
  AiExpertScopeValues,
  ConductorScopeConfigurationModel,
  ConductorScopePatchJson,
  OrganizationAiExpertInfo,
  ScopeRow,
} from './types';

interface AgentConfigScopeEditorProps {
  organizationId: number;
  scope: ScopeRow;
  onBack: () => void;
}

/**
 * Owns one scope and renders the sections that configure it.
 *
 * A full screen with a Back button rather than a dialog, and **no page-level Save**: each
 * section writes through its own sparse PATCH, so a section that fails leaves the others
 * alone and there is no aggregate payload to keep consistent.
 */
const AgentConfigScopeEditor: React.FC<AgentConfigScopeEditorProps> = ({ organizationId, scope, onBack }) => {
  const baseUrl = `/sysconfig/api/v1/organizations/${organizationId}/conductor_configuration/scope`;
  const expertsBaseUrl = `/sysconfig/api/v1/organizations/${organizationId}/ai_experts`;
  // The scope key travels as query params on the read. `sub_organization_id` is omitted
  // entirely for a flat organization rather than sent as an empty string, which the query
  // schema would reject.
  const queryString =
    scope.sub_organization_id === null
      ? `lob=${encodeURIComponent(scope.lob)}`
      : `lob=${encodeURIComponent(scope.lob)}&sub_organization_id=${scope.sub_organization_id}`;

  const { data, isLoading, isError, reloadData } = useDataFetcher<ConductorScopeConfigurationModel>(
    `${baseUrl}?${queryString}`
  );
  // The experts section reads the same scope key, on its own resource: the catalogue says which
  // experts exist, the scope read says what this scope does with them. An expert missing from the
  // rows has never been configured here, which the card renders as off.
  const {
    data: experts,
    isLoading: isLoadingExperts,
    isError: isErrorExperts,
  } = useDataFetcher<OrganizationAiExpertInfo[]>(expertsBaseUrl);
  const {
    data: expertScopes,
    isLoading: isLoadingExpertScopes,
    isError: isErrorExpertScopes,
    reloadData: reloadExpertScopes,
  } = useDataFetcher<AiExpertScopeConfigurationInfo[]>(`${expertsBaseUrl}/scopes?${queryString}`);
  // Errors already open the shared error dialog through reportAxiosError; this is the success
  // side, which the feature otherwise had no feedback for at all.
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();

  /**
   * Applies a sparse patch and refreshes.
   *
   * The scope is addressed by the editor rather than by the caller, so a section only has to
   * name the fields it owns. Resolves to `undefined` on failure — `reloadData` reports its
   * own error and never throws — which is what dialogs read to decide whether to close.
   */
  const handlePatch = async (patch: Partial<ConductorScopePatchJson>): Promise<unknown> => {
    try {
      await axios.patch(baseUrl, {
        lob: scope.lob,
        sub_organization_id: scope.sub_organization_id,
        ...patch,
      });
      const refreshed = await reloadData();
      if (refreshed !== undefined) {
        // Named per field so the confirmation says what landed: each section saves on its own,
        // so a generic "Saved" would not tell the operator which one did.
        showSnackbar(
          patch.is_enabled === undefined
            ? copies.editor.savedChanges
            : patch.is_enabled
              ? copies.editor.savedConductorOn
              : copies.editor.savedConductorOff
        );
      }
      return refreshed;
    } catch (error) {
      reportAxiosError(error);
      return undefined;
    }
  };

  /**
   * Upserts one expert's row for this scope and refreshes.
   *
   * A sibling of `handlePatch` rather than a widening of it: that one is a sparse PATCH against the
   * conductor resource, this one a whole-row PUT against the expert's. The scope is addressed here
   * for the same reason -- the dialog only has to name the fields it carries.
   */
  const handleSaveExpert = async (expertKey: string, values: AiExpertScopeValues): Promise<unknown> => {
    try {
      await axios.put(`${expertsBaseUrl}/scopes/${expertKey}`, {
        lob: scope.lob,
        sub_organization_id: scope.sub_organization_id,
        ...values,
      });
      const refreshed = await reloadExpertScopes();
      if (refreshed !== undefined) {
        showSnackbar(copies.experts.saved);
      }
      return refreshed;
    } catch (error) {
      reportAxiosError(error);
      return undefined;
    }
  };

  return (
    <div className="flex min-h-[50vh] flex-col gap-16 bg-white p-24">
      <div className="flex flex-col gap-8">
        <Button className="self-start" variant="text" color="primary" startIcon={<ChevronLeftIcon />} onClick={onBack}>
          {copies.editor.back}
        </Button>
        <Text variant={Text.VARIANTS.XL} weight={Text.WEIGHTS.SEMI_BOLD}>
          {`${scope.sub_organization_name} / ${scope.lob}`}
        </Text>
        <div className="gap-6 flex flex-wrap items-center">
          <AgentConfigChip label={scope.sub_organization_name} tone={AGENT_CHIP_TONE.SUB_ORG} />
          <AgentConfigChip label={scope.lob} tone={AGENT_CHIP_TONE.LOB} />
          {data?.last_updated_datetime ? (
            <AgentConfigChip
              label={copies.editor.lastSaved(
                serverDateTimeToLocal(data.last_updated_datetime),
                data.last_updated_by_username ?? null
              )}
              tone={AGENT_CHIP_TONE.NEUTRAL}
            />
          ) : null}
        </div>
      </div>
      {isLoading || isError || !data ? (
        <SkeletonTable rowsCount={5} columnsCount={3} isError={isError} />
      ) : (
        <ConductorSection organizationId={organizationId} scope={scope} configuration={data} onPatch={handlePatch} />
      )}
      {/* Gated on its own reads rather than on the conductor's: the sections save separately, so a
          section whose data did not load leaves the others alone. */}
      {isLoadingExperts || isErrorExperts || isLoadingExpertScopes || isErrorExpertScopes ? (
        <SkeletonTable rowsCount={3} columnsCount={3} isError={isErrorExperts || isErrorExpertScopes} />
      ) : (
        <ExpertsCard
          experts={experts ?? []}
          scopeRows={expertScopes ?? []}
          // Read from the same conductor row the Conductor section above renders, so the two
          // cannot disagree about whether the conductor runs here.
          isConductorEnabledForScope={Boolean(data?.is_enabled)}
          onSaveExpert={handleSaveExpert}
        />
      )}
      <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
    </div>
  );
};

export default AgentConfigScopeEditor;
